import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
  Control,
  Controller,
  UseFieldArrayRemove,
  UseFormClearErrors,
  UseFormSetError,
  useWatch,
} from 'react-hook-form';

import styled, { css } from '@mint-lib/styled';
import type { Identifier } from 'dnd-core';

import { Box, Icon, IconButton, Input, NumberField } from '@myn/mui';

import { CustomColumnFormat } from '../../../../../types/builderTableView.js';
import {
  DECIMAL_SCALE,
  DimensionsDragType,
  MIN_OPTIONS_COUNT,
} from '../../../EditModal.constants.js';
import { isUnique } from '../../../EditModal.utils.js';
import { CustomColumn } from '../CustomColumnForm.types.js';
import { DraggableOption } from './OptionField.types.js';

type Props = {
  control: Control<CustomColumn, 'options'>;
  onRemove: UseFieldArrayRemove;
  onMove: (indexFrom: number, indexTo: number) => void;
  setError: UseFormSetError<CustomColumn>;
  clearErrors: UseFormClearErrors<CustomColumn>;
  isSubmitted: boolean;
  index: number;
  className?: string;
};

const OldOptionField = ({
  control,
  onRemove,
  index,
  onMove,
  setError,
  clearErrors,
  isSubmitted,
  className,
}: Props) => {
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [isOverAfter, setIsOverAfter] = useState<boolean>(false);

  const values = useWatch({ control });
  const name = values.options?.[index]?.name ?? '';
  const isNumericOption =
    values.format === CustomColumnFormat.number ||
    values.format === CustomColumnFormat.percentage ||
    values.format === CustomColumnFormat.currency;

  const [{ isDragging }, dragRef, preview] = useDrag<
    DraggableOption,
    void,
    { isDragging: boolean }
  >({
    type: DimensionsDragType.SELECT_OPTION,
    item: {
      type: DimensionsDragType.SELECT_OPTION,
      index,
      name,
      isNumericOption,
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ handlerId, isOver }, drop] = useDrop<
    DraggableOption,
    void,
    { handlerId: Identifier | null; isOver: boolean }
  >({
    accept: [DimensionsDragType.SELECT_OPTION],
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
        isOver: monitor.isOver(),
      };
    },
    drop(dragItem: DraggableOption) {
      onMove(dragItem.index, index);
    },
    hover(dragItem: DraggableOption) {
      setIsOverAfter(dragItem.index > index);
    },
  });

  const handleRemove = useCallback(() => {
    if (Number(values.options?.length) > MIN_OPTIONS_COUNT) {
      onRemove(index);
    }
  }, [index, values, onRemove]);

  const isUniqueName = useMemo(() => {
    const names = values.options?.map(({ name }) => name ?? '') ?? [];

    const name = values.options?.[index].name;
    return name && isUnique(name, names);
  }, [values, index]);

  useEffect(() => {
    if (isSubmitted && !isUniqueName) {
      setError(`options.${index}.name` as 'option', { message: 'isNotUnique' });
      return;
    }
    clearErrors(`options.${index}.name` as 'option');
  }, [isSubmitted, isUniqueName]);

  const prepareNumberFieldValue = (value?: string | number) => {
    if (!value && value !== 0) {
      return null;
    }

    const numericValue = Number(value);

    return Number.isNaN(numericValue) ? null : numericValue;
  };

  const handleValueChange = (
    value: number | null,
    onChange: (value: string) => void,
  ) => {
    const preparedValue =
      !value && value !== 0 ? '' : value.toFixed(DECIMAL_SCALE);

    onChange(preparedValue);
  };

  drop(preview(previewRef));

  return (
    <Controller
      control={control}
      name={`options.${index}.name` as 'option'}
      render={({ field, fieldState }) =>
        values.format === CustomColumnFormat.text ? (
          <FieldWrapper
            className={className}
            ref={previewRef}
            data-handler-id={handlerId}
            isDragging={isDragging}
            isOver={isOver}
            isOverAfter={isOverAfter}
          >
            <Input
              fullWidth
              size="small"
              startAdornment={
                <div
                  // @ts-expect-error Wrong type in a lib
                  ref={dragRef}
                >
                  <Icon component="Draggable" />
                </div>
              }
              disabled={isSubmitted}
              endAdornment={
                <IconButton
                  variant="icon"
                  icon="Close"
                  onClick={handleRemove}
                  size="small"
                />
              }
              error={
                Boolean(fieldState.error) || (isSubmitted && !isUniqueName)
              }
              value={field.value}
              onChange={field.onChange}
            />
          </FieldWrapper>
        ) : (
          <FieldWrapper
            className={className}
            ref={previewRef}
            data-handler-id={handlerId}
            isDragging={isDragging}
            isOver={isOver}
            isOverAfter={isOverAfter}
          >
            <NumberField
              value={prepareNumberFieldValue(field.value)}
              onChange={(value) => {
                handleValueChange(value, field.onChange);
              }}
              disabled={isSubmitted}
              maxDecimals={DECIMAL_SCALE}
              restFormatProps={{
                min: 0,
                minDecimals: DECIMAL_SCALE,
              }}
              error={
                Boolean(fieldState.error) || (isSubmitted && !isUniqueName)
              }
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <div
                    // @ts-expect-error Wrong type in a lib
                    ref={dragRef}
                  >
                    <Icon component="Draggable" />
                  </div>
                ),
                endAdornment: (
                  <IconButton
                    variant="icon"
                    icon="Close"
                    size="small"
                    onClick={handleRemove}
                  />
                ),
              }}
            />
          </FieldWrapper>
        )
      }
    />
  );
};

const FieldWrapper = styled(Box)<{
  isDragging: boolean;
  isOverAfter: boolean;
  isOver: boolean;
}>`
  opacity: ${({ isDragging }) => (isDragging ? '0' : '1')};
  position: relative;
  ${({ isOverAfter, isOver, theme }) => {
    const top = isOverAfter ? '-4px' : 'calc(100% + 2px)';
    return isOver
      ? css`
          &::after {
            position: absolute;
            content: '';
            left: 0;
            top: ${top};
            height: 2px;
            width: 100%;
            background: ${theme.palette.primary.main};
          }
        `
      : null;
  }}
`;

export default memo(OldOptionField);
